import { PureComponent, type ReactElement, type ReactNode } from 'react';

import { OBJECT_TYPE_COLLECTIONS } from '@iconik-io/iconik-api/src/types/object_types';

import {
  ACL_TYPE_OWN,
  ACL_TYPE_PROPAGATED,
} from '../../ducks/common_acls/constants';
import type {
  CommonAclsPrefix,
  EntityGroupAcl,
  EntityUserAcl,
} from '../../ducks/common_acls/types';
import type { AppConnectedProps } from '../../store/types';
import Button from '../../ui/button/button';
import CheckboxFieldWrapper from '../../ui/forms/field_wrappers/checkbox/checkbox';
import AclsControls from './acls_controls/acls_controls';
import type { AclsFormData } from './acls_form/acls_form';
import connector from './connector';
import type AclsListContextMenu from './context_menu';

import styles from './entity_acls.module.css';

export type EntityAclsFormData = {
  user_ids: string[];
  group_ids: string[];
  permissions: string[];
};

type Props = AppConnectedProps<typeof connector> & {
  onValidAcls?: (data: EntityAclsFormData, reset: () => void) => void;
  leftButton?: ReactElement;
  stickyForm?: boolean;
  isModal?: boolean;
  contextMenu?: typeof AclsListContextMenu;
  useEmptyState?: boolean;
};

type State = {
  canSubmit: boolean;
  loading: boolean;
};

export class EntityAclsPanel extends PureComponent<Props, State> {
  state: State = {
    canSubmit: false,
    loading: false,
  };

  componentDidMount(): void {
    this.fetchData();
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (
      'objectId' in prevProps &&
      'objectId' in this.props &&
      prevProps.objectId !== this.props.objectId
    ) {
      this.fetchData();
    }
  }

  fetchData = (): void => {
    if ('objectId' in this.props && this.props.objectId) {
      const { getEntityAcls, getEntityCurrentAcls, objectId, aclsType } =
        this.props;
      this.setState({ loading: true });
      Promise.all([
        getEntityAcls(aclsType, objectId),
        getEntityCurrentAcls(aclsType, objectId),
      ]).finally(() => {
        this.setState({ loading: false });
      });
    }
  };

  validAcls = (form: AclsFormData, reset = () => {}): void => {
    if (!this.state.canSubmit) {
      return;
    }
    const { permissions, propagate, ...rest } = form;
    const data: EntityAclsFormData = { ...rest, permissions: [] };
    data.permissions = permissions.map((v) => v.value);
    if (this.props.onValidAcls) {
      this.props.onValidAcls(data, reset);
    } else {
      reset();
    }
    let entity: string;
    let entitiesLength = 0;
    if ('entities' in this.props) {
      // Grouped by the entity's own aclsType so the prefix keeps its type;
      // lodash groupBy would widen the key to a plain string.
      const idsByPrefix = new Map<CommonAclsPrefix, string[]>();
      this.props.entities.forEach(({ aclsType, id }) => {
        const entityIds = idsByPrefix.get(aclsType) ?? [];
        entityIds.push(id);
        idsByPrefix.set(aclsType, entityIds);
      });
      idsByPrefix.forEach((ids, prefix) => {
        this.props.addEntityAcls(prefix, ids, {
          ...data,
          type: propagate ? ACL_TYPE_PROPAGATED : ACL_TYPE_OWN,
        });
      });

      entity = [...idsByPrefix.keys()].join(', ');
      entitiesLength = this.props.entities.length;
    } else {
      entity = this.props.aclsType;
      if (this.props.objectId) {
        this.props.addEntityAcls(this.props.aclsType, [this.props.objectId], {
          ...data,
          type: propagate ? ACL_TYPE_PROPAGATED : ACL_TYPE_OWN,
        });
      }
    }
    this.props.segmentTrackEvent({
      eventName: 'ACL Modified',
      properties: {
        Entity: entity,
        Bulk: Boolean(entitiesLength),
      },
    });
  };

  onChange = (form: AclsFormData): void => {
    const userIdsEmpty = (form.user_ids?.length ?? 0) === 0;
    const groupIdsEmpty = (form.group_ids?.length ?? 0) === 0;

    if ((!userIdsEmpty || !groupIdsEmpty) && !this.state.canSubmit) {
      this.setState({ canSubmit: true });
    }

    if (userIdsEmpty && groupIdsEmpty && this.state.canSubmit) {
      this.setState({ canSubmit: false });
    }
  };

  render(): ReactNode {
    const {
      defaultExpanded,
      permissions,
      acls,
      roles,
      leftButton,
      stickyForm,
      isModal,
      contextMenu,
      useEmptyState,
      additionalTeamOptions,
    } = this.props;

    let usersElementAcls: EntityUserAcl[] = [];
    let groupsElementAcls: EntityGroupAcl[] = [];
    let aclsType: CommonAclsPrefix | undefined;
    let objectId: string | undefined;

    if ('objectId' in this.props) {
      aclsType = this.props.aclsType;
      objectId = this.props.objectId;
      usersElementAcls = objectId ? (acls.entityUsersAcls[objectId] ?? []) : [];
      groupsElementAcls = objectId
        ? (acls.entityGroupsAcls[objectId] ?? [])
        : [];
    }

    const propagateControl =
      aclsType === OBJECT_TYPE_COLLECTIONS ? (
        <div className={styles.propagate_block}>
          <CheckboxFieldWrapper
            className={styles.propagate_field}
            name="propagate"
            label="Propagate to all content automatically"
            labelWidth="240px"
            disabled={!roles.canAddPropagatingAcls}
          />
          <div className={styles.propagate_help}>
            {roles.canAddPropagatingAcls
              ? 'This will always apply this ACL to everything in this collection'
              : 'You don’t have the necessary roles to enable propagating ACLs'}
          </div>
        </div>
      ) : (
        <div />
      );

    return (
      <AclsControls
        contextMenu={contextMenu}
        aclsType={aclsType}
        aclsObjectId={objectId}
        visibleAcl
        writeAcl={roles.canWriteAcls && permissions['change-acl']}
        deleteAcl={roles.canWriteAcls && permissions['change-acl']}
        usersElementAcls={usersElementAcls}
        groupsElementAcls={groupsElementAcls}
        validAcls={this.validAcls}
        defaultExpanded={defaultExpanded}
        edit={acls.edit}
        onChange={this.onChange}
        stickyForm={stickyForm}
        isModal={isModal}
        loading={this.state.loading}
        useEmptyState={useEmptyState}
        additionalTeamOptions={additionalTeamOptions}
      >
        {leftButton ? propagateControl : null}
        <div className={styles.bottom_buttons}>
          {leftButton ?? propagateControl}
          <div className={styles.confirm_group}>
            <Button
              noBorder
              variant="success"
              type="submit"
              disabled={!this.state.canSubmit}
              wideBasis={false}
            >
              Add
            </Button>
          </div>
        </div>
      </AclsControls>
    );
  }
}

export default connector(EntityAclsPanel);
